import {
    LiveMatchListActions,
    LiveMatchListSetFilterDataAction,
    LiveGroupsMatchListSetFilterDataAction,
    LiveGroupsOverviewMatchListSetFilterDataAction,
    LiveMatchListSetLoadingData,
    LiveMatchListStoreModel,
} from './live-match-list.constants';
import { ACTION_CORE_API_SUB_DUMP, ACTION_CORE_API_SUB_UPDATE, AppAction } from '@om-fe/store';
import { filterItemsFromRecords, filterItemsSelectorFromRecords } from '../../utils/filter-items-from-records';
import applyUpdatesForMatchList from '../../utils/apply-updates-for-match-list';
import { getLiveMatchListSliceName, getLiveGroupsMatchListSliceName } from './live-match-list.utils';
import { getLiveMatchListTotalRecords } from '../../utils/match-list-limit';
import { AggregatorOneOfTypeModel } from '@om-fe/models';
import { UpdateAction } from '@om-fe/api';
import { isBrowser } from '@om-fe/utils';

const initialState: LiveMatchListStoreModel = {
    fetchedFromBrowser: isBrowser(),
    ready: false,
    limit: 0,
    totalRecords: 0,
    isLoadingMoreData: false,
    isLoadingData: true,
    discipline: undefined,
    filteredEventInfos: {},
    filteredMainMarketsList: [],
    filteredMainMarkets: {},
    filteredMarketGroupsOverviewList: [],
    filteredMarketGroupsOverview: {},
    filteredEventStatistics: [],
    filteredMarketAndOutcomeRelation: {},
    filteredOutcomes: {},
    filteredBettingOffers: {},
    filteredMatchesWithEvents: {},
    filteredMatchesIds: [],
    filteredStreamingProviderEventRelation: {},
    marketsWithOutcomesAndBettingOffers: {},
    filteredMainOutrightsMarketsList: [],
};

export const liveMatchListReducerCreator = (instanceId: string) => (
    state: LiveMatchListStoreModel = initialState,
    action: AppAction,
): LiveMatchListStoreModel => {
    const slice: string = getLiveMatchListSliceName(instanceId);

    const { meta } = action;

    if (!meta || meta.slice !== slice) {
        return state;
    }

    switch (action.type) {

        case LiveMatchListActions.SetLoadingData: {
            const { payload: { isLoadingMoreData } } = action as LiveMatchListSetLoadingData;

            return {
                ...state,
                isLoadingData: !isLoadingMoreData,
                isLoadingMoreData,
            };
        }

        case LiveMatchListActions.SetFilterData: {
            const { payload } = action as LiveMatchListSetFilterDataAction;

            return {
                ...state,
                limit: payload.limit,
                disciplineId: payload.disciplineId,
                locationId: payload.locationId,
                isDefault: payload.isDefault,
                mainMarketsLimit: payload.mainMarketsLimit,
                eventCategoryId: payload.eventCategoryId,
                isAuthenForTopic: payload.isAuthenForTopic,
            };
        }
        case ACTION_CORE_API_SUB_DUMP: {
            const { payload } = action as UpdateAction<AggregatorOneOfTypeModel>;

            const records: AggregatorOneOfTypeModel[] = payload.records;

            const totalRecords: number = getLiveMatchListTotalRecords(payload.records);

            return {
                ...state,
                ...filterItemsFromRecords(records),
                fetchedFromBrowser: isBrowser(),
                totalRecords,
                ready: true,
                isLoadingData: false,
                isLoadingMoreData: false,
            };
        }
        case ACTION_CORE_API_SUB_UPDATE: {
            const { payload } = action as UpdateAction<AggregatorOneOfTypeModel>;

            const totalRecords: number = getLiveMatchListTotalRecords(payload.records);

            return {
                ...state,
                ...applyUpdatesForMatchList(payload, state),
                totalRecords,
                isLoadingMoreData: false,
            };
        }
        default:
            return state;
    }
};

export const liveGroupsMatchListReducerCreator = (instanceId: string) => (
    state: LiveMatchListStoreModel = initialState,
    action: AppAction,
): LiveMatchListStoreModel => {
    const slice: string = getLiveGroupsMatchListSliceName(instanceId);

    const { meta } = action;

    if (!meta || meta.slice !== slice) {
        return state;
    }

    switch (action.type) {
        case LiveMatchListActions.SetLoadingData: {
            const { payload: { isLoadingMoreData } } = action as LiveMatchListSetLoadingData;
            
            return {
                ...state,
                isLoadingData: !isLoadingMoreData,
                isLoadingMoreData,
            };
        }

        case LiveMatchListActions.SetFilterData: {
            const { payload } = action as LiveGroupsMatchListSetFilterDataAction;

            return {
                ...state,
                limit: payload.limit,
                disciplineId: payload.disciplineId,
                locationId: payload.locationId,
                isDefault: payload.isDefault,
                mainMarketsLimit: payload.mainMarketsLimit,
                eventCategoryId: payload.eventCategoryId,
                marketGroupsOverviewNumber: payload.marketGroupsOverviewNumber,
                isAuthenForTopic: payload.isAuthenForTopic,
            };
        }
        case ACTION_CORE_API_SUB_DUMP: {
            const { payload } = action as UpdateAction<AggregatorOneOfTypeModel>;

            const records: AggregatorOneOfTypeModel[] = payload.records;

            const totalRecords: number = getLiveMatchListTotalRecords(payload.records);

            return {
                ...state,
                ...filterItemsSelectorFromRecords(records),
                fetchedFromBrowser: isBrowser(),
                totalRecords,
                ready: true,
                isLoadingData: false,
                isLoadingMoreData: false,
            };
        }
        case ACTION_CORE_API_SUB_UPDATE: {
            const { payload } = action as UpdateAction<AggregatorOneOfTypeModel>;

            const totalRecords: number = getLiveMatchListTotalRecords(payload.records);

            return {
                ...state,
                ...applyUpdatesForMatchList(payload, state),
                totalRecords,
                isLoadingMoreData: false,
            };
        }
        default:
            return state;
    }
};

export const liveGroupsOverviewMatchListReducerCreator = (instanceId: string) => (
    state: LiveMatchListStoreModel = initialState,
    action: AppAction,
): LiveMatchListStoreModel => {
    const slice: string = getLiveMatchListSliceName(instanceId);

    const { meta } = action;

    if (!meta || meta.slice !== slice) {
        return state;
    }

    switch (action.type) {
        case LiveMatchListActions.SetLoadingData: {
            const { payload: { isLoadingMoreData } } = action as LiveMatchListSetLoadingData;

            return {
                ...state,
                isLoadingData: !isLoadingMoreData,
                isLoadingMoreData,
            };
        }

        case LiveMatchListActions.SetFilterData: {
            const { payload } = action as LiveGroupsOverviewMatchListSetFilterDataAction;

            return {
                ...state,
                limit: payload.limit,
                disciplineId: payload.disciplineId,
                locationId: payload.locationId,
                isDefault: payload.isDefault,
                mainMarketsLimit: payload.mainMarketsLimit,
                eventCategoryId: payload.eventCategoryId,
                marketGroupsOverviewIds: payload.marketGroupsOverviewIds,
                isAuthenForTopic: payload.isAuthenForTopic,
            };
        }
        case ACTION_CORE_API_SUB_DUMP: {
            const { payload } = action as UpdateAction<AggregatorOneOfTypeModel>;

            const records: AggregatorOneOfTypeModel[] = payload.records;

            const totalRecords: number = getLiveMatchListTotalRecords(payload.records);

            return {
                ...state,
                ...filterItemsSelectorFromRecords(records),
                fetchedFromBrowser: isBrowser(),
                totalRecords,
                ready: true,
                isLoadingData: false,
                isLoadingMoreData: false,
            };
        }
        case ACTION_CORE_API_SUB_UPDATE: {
            const { payload } = action as UpdateAction<AggregatorOneOfTypeModel>;

            const totalRecords: number = getLiveMatchListTotalRecords(payload.records);

            return {
                ...state,
                ...applyUpdatesForMatchList(payload, state),
                totalRecords,
                isLoadingMoreData: false,
            };
        }
        default:
            return state;
    }
};
